import { ParameterValues } from "calculation/parameter";
import { useState } from "react";
import { ReactElement } from "react";
import { useHistory } from "react-router-dom";
import { URL } from "myconstants";
import { ModalSaveAs } from "./ModalSaveAs";
import { Action, State } from "../../state/state";
import { ParameterSetDto, ParameterSetNameDto, ScenarioDto } from "dtos/dtos";
import { exportParametersCsv } from "reports/reports";
import { ParametersTable } from "components/zoning-plan-common/ParametersTable";
import { DownloadButton } from "components/common/DownloadButton";
import { NoData } from "components/common/NoData";
import { Select } from "components/common/Select";
import { useEffect } from "react";
import { Button } from "components/common/Button";
import { StatusBar } from "components/common/StatusBar";
import { Overlay } from "components/common/Overlay";
import { ModalDelete } from "./ModalDelete";
import { ModalRename } from "./ModalRename";
import moment from "moment";

export function Parameters(props: {
  dispatch: (action: Action) => void;
  state: State;
}): ReactElement {
  const dispatch = props.dispatch;
  const state = props.state;
  const scenarioNames = state.zoningPlanDraft.scenarioNames?.filter(
    (s) => s.status !== "inKraft"
  );
  const parameterSetNames = state.zoningPlanDraft.parameterSetNames;
  const selectedScenario = state.zoningPlanDraft.selectedScenario;
  const selectedParameterSet = state.zoningPlanDraft.selectedParameterSet;
  const [loadScenarioId, setLoadScenarioId] = useState<string>();
  const [loadParameterSetId, setLoadParameterSetId] = useState<string>();
  const [showStatusBar, setShowStatusBar] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSaveAsModal, setShowSaveAsModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [parameterValues, setParameterValues] = useState<ParameterValues>();
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const history = useHistory();

  async function saveSelectedParameterSet() {
    if (selectedParameterSet !== undefined && parameterValues !== undefined) {
      selectedParameterSet.parameterValues =
        parameterValues.createParameterValueDtos();
      selectedParameterSet.modifiedAt = moment().format();
      try {
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersets/${selectedParameterSet.id}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            method: "PUT",
            body: JSON.stringify(selectedParameterSet),
            credentials: "include",
          }
        );

        dispatch({
          type: "saveParameterSet",
          data: { parameterSet: selectedParameterSet },
        });
        setShowStatusBar(true);
        setTimeout(() => setShowStatusBar(false), 1500);
        setHasPendingChanges(false);
      } catch {
        history.replace(URL.error);
      }
    }
  }

  async function deleteSelectedParameterSet() {
    if (selectedParameterSet !== undefined) {
      try {
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersets/${selectedParameterSet.id}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        dispatch({
          type: "deleteParameterSet",
          data: { parameterSetId: selectedParameterSet.id },
        });
        setShowStatusBar(true);
        setTimeout(() => setShowStatusBar(false), 1500);
      } catch {
        history.replace(URL.error);
      }
    }
  }

  async function renameSelectedParameterSet(newName: string) {
    if (selectedParameterSet !== undefined) {
      try {
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersets/${selectedParameterSet.id}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            method: "PATCH",
            body: JSON.stringify({
              name: newName,
            }),
            credentials: "include",
          }
        );
        dispatch({
          type: "renameParameterSet",
          data: { parameterSetId: selectedParameterSet.id, newName: newName },
        });
        setShowStatusBar(true);
        setTimeout(() => setShowStatusBar(false), 1500);
      } catch {
        history.replace(URL.error);
      }
    }
  }

  async function saveSelectedParameterSetAs(newParameterSetName: string) {
    if (
      selectedParameterSet !== undefined &&
      parameterValues !== undefined &&
      state.user.user !== undefined
    ) {
      const copyParameterValuesDtos =
        parameterValues.createParameterValueDtos();

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersets`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            method: "POST",
            body: JSON.stringify({
              name: newParameterSetName,
              userId: state.user.user.id,
              scenarioId: selectedParameterSet.scenarioId,
              readonly: false,
              active: false,
              parameterValues: copyParameterValuesDtos,
            }),
            credentials: "include",
          }
        );

        if (response.ok) {
          const addedParameterSetDto: ParameterSetDto = await response.json();
          dispatch({
            type: "addParameterSet",
            data: { parameterSet: addedParameterSetDto },
          });
        }
      } catch {
        history.replace(URL.error);
      }
    }
  }

  useEffect(() => {
    async function loadScenario(scenarioId: string) {
      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/scenarios/id/${scenarioId}`,
          {
            credentials: "include",
          }
        );

        if (response.ok) {
          const scenario: ScenarioDto = await response.json();
          const parameterSetNamesResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/parametersetnames/${scenario.id}`,
            {
              credentials: "include",
            }
          );

          if (parameterSetNamesResponse.ok) {
            const parameterSetNames: ParameterSetNameDto[] =
              await parameterSetNamesResponse.json();

            dispatch({
              type: "setSelectedScenarioDraft",
              data: {
                scenario: scenario,
                parameterSetNames: parameterSetNames,
              },
            });
          }
        }
      } catch {
      } finally {
        setLoading(false);
      }
    }

    if (loadScenarioId !== undefined) {
      loadScenario(loadScenarioId);
    }
  }, [loadScenarioId, dispatch]);

  useEffect(() => {
    async function load(parameterSetId: string) {
      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersets/id/${parameterSetId}`,
          {
            credentials: "include",
          }
        );
        if (response.ok) {
          const parameterSet: ParameterSetDto = await response.json();
          dispatch({
            type: "setSelectedParameterSetDraft",
            data: { parameterSet: parameterSet },
          });
        }
      } catch {
      } finally {
        setLoading(false);
        setLoadParameterSetId(undefined);
      }
    }

    if (loadParameterSetId !== undefined) {
      load(loadParameterSetId);
    }
  }, [loadParameterSetId, dispatch]);

  useEffect(() => {
    if (
      selectedScenario === undefined &&
      scenarioNames !== undefined &&
      scenarioNames.length === 1
    ) {
      setLoadScenarioId(scenarioNames[0].id.toString());
    }
  }, [selectedScenario, scenarioNames]);

  useEffect(() => {
    if (selectedParameterSet === undefined) {
      if (parameterSetNames !== undefined && parameterSetNames.length === 1) {
        setLoadParameterSetId(parameterSetNames[0].id.toString());
      } else {
        setParameterValues(undefined);
      }
    } else {
      setParameterValues(
        new ParameterValues(selectedParameterSet.parameterValues)
      );
    }
  }, [selectedParameterSet, parameterSetNames]);

  useEffect(() => {
    setHasPendingChanges(false);
  }, [selectedParameterSet]);

  const hasData =
    state.parameters !== undefined &&
    scenarioNames !== undefined &&
    scenarioNames.length > 0;

  return (
    <div className="flex flex-col text-gray-700 w-full">
      <Overlay show={loading} text="Lade Daten ..." />
      <ModalSaveAs
        initialParameterSetName={
          selectedParameterSet !== undefined
            ? selectedParameterSet.name + " - Kopie"
            : ""
        }
        visible={showSaveAsModal}
        setShowModal={setShowSaveAsModal}
        saveAction={(parameterSetName: string) =>
          saveSelectedParameterSetAs(parameterSetName)
        }
      />
      <ModalRename
        initialParameterSetName={
          selectedParameterSet !== undefined ? selectedParameterSet.name : ""
        }
        visible={showRenameModal}
        setShowModal={setShowRenameModal}
        renameAction={(parameterSetName: string) =>
          renameSelectedParameterSet(parameterSetName)
        }
      />
      <ModalDelete
        visible={showDeleteModal}
        setShowModal={setShowDeleteModal}
        deleteAction={() => deleteSelectedParameterSet()}
      />
      <StatusBar show={showStatusBar} text="Gespeichert" />
      {!hasData && <NoData />}
      {hasData && (
        <div className="flex flex-col space-y-4">
          <div className="mb-4">
            <h1 className="text-lg font-bold mb-2">Kennwerte</h1>
            <p className="my-2">
              Sie können die standardmässigen Kennwerte laden, editieren und für
              spätere Zwecke speichern. Diese Kennzahlen dienen dann als Basis
              zur Berechnung der Bauzonenkapazitäten im nächsten Schritt.
            </p>
            <p className="my-2">
              <span className="font-bold">
                Anwendungsbeispiel "Vergleich Zonenplan mit zwei verschiedenen
                Kennzahlen":
              </span>
              <br />
              Editieren Sie die Kennzahlen und speichern diese als "Szenario
              Kennzahlen 1". Editieren Sie erneut Kennzahlen und speichern diese
              ab unter "Szenario Kennzahlen 2". Im nächsten Schritt wählen Sie
              zweimal den identischen Zonenplan (mit / ohne Vorwirkung), jedoch
              einmal mit "Szenario Kennzahlen 1" und "Szenario Kennzahlen 2".
            </p>
          </div>
          <Select
            header={{ displayValue: "Zonenplan auswählen", enabled: false }}
            options={scenarioNames?.map((s) => {
              return {
                value: s.id.toString(),
                displayValue: `${s.name} ${s.status}`,
              };
            })}
            value={selectedScenario?.id.toString()}
            width="w-80"
            visible={true}
            onChange={(value) => setLoadScenarioId(value)}
          />
          <Select
            header={{ displayValue: "Kennwerte auswählen", enabled: false }}
            options={parameterSetNames?.map((ps) => {
              return { value: ps.id.toString(), displayValue: ps.name };
            })}
            value={selectedParameterSet?.id.toString()}
            width="w-80"
            visible={true}
            onChange={(value) => setLoadParameterSetId(value)}
          />
          {selectedScenario !== undefined && parameterValues !== undefined && (
            <>
              <ParametersTable
                parameters={state.parameters!}
                parameterValues={parameterValues}
                scenario={selectedScenario}
                readonlyParameterSet={
                  selectedParameterSet === undefined
                    ? true
                    : selectedParameterSet.readonly
                }
                onChange={() => setHasPendingChanges(true)}
              />
              {!hasPendingChanges && <div className="h-6"></div>}
              {hasPendingChanges && (
                <span className="h-6 text-red-400 font-bold">
                  Sie haben ungespeicherte Änderungen!
                </span>
              )}
              <div className="mt-4 flex flex-row space-x-4">
                <Button
                  className={`${
                    hasPendingChanges ? "bg-blue-400" : "bg-blue-200"
                  }`}
                  onClick={() => saveSelectedParameterSet()}
                  disabled={hasPendingChanges ? false : true}
                >
                  Speichern
                </Button>
                <Button
                  className={`${
                    hasPendingChanges ? "bg-blue-400" : "bg-blue-200"
                  }`}
                  onClick={() => {
                    if (selectedParameterSet !== undefined) {
                      setParameterValues(
                        new ParameterValues(
                          selectedParameterSet.parameterValues
                        )
                      );
                      setHasPendingChanges(false);
                    }
                  }}
                  disabled={hasPendingChanges ? false : true}
                >
                  Rückgängig
                </Button>
                <Button
                  className="bg-blue-400"
                  onClick={() => setShowSaveAsModal(true)}
                >
                  Speichern unter
                </Button>
                <Button
                  className="bg-blue-400"
                  onClick={() => setShowRenameModal(true)}
                >
                  Umbenennen
                </Button>
                <Button
                  className="bg-red-400"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Löschen
                </Button>
              </div>
              <div className="mt-4 flex flex-col space-y-2">
                <h2 className="font-bold">Downloads</h2>
                <DownloadButton
                  label="CSV"
                  text="Kennwerte im Detail"
                  width="w-52"
                  onClick={() =>
                    exportParametersCsv(
                      state.parameters!,
                      parameterValues!,
                      selectedScenario!,
                      selectedParameterSet === undefined
                        ? ""
                        : selectedParameterSet.name,
                      state.selectedMunicipality!.gemeinde
                    )
                  }
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
